import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { useSettings } from '@/context/SettingsContext';
import api from '@/lib/api';
import { CheckCircle, ArrowLeft, Package } from 'lucide-react';
import { formatDate } from '@/utils/dateFormat';

export default function OrderConfirmation() {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const { settings: s } = useSettings();

  useEffect(() => {
    api.get(`/orders/${orderId}/public`).then(r => setOrder(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [orderId]);

  if (loading) return <div className="pt-20 min-h-screen flex items-center justify-center"><div className="animate-spin h-8 w-8 border-2 border-[#0E518D] border-t-transparent rounded-full" /></div>;

  if (!order) return (
    <div className="pt-20 min-h-screen bg-[#F7F9FC] flex items-center justify-center">
      <div className="text-center">
        <p className="text-lg text-slate-500">Bestellung nicht gefunden.</p>
        <Link to="/"><Button className="mt-4 bg-[#0E518D] text-white rounded-sm uppercase tracking-wide">Zurück</Button></Link>
      </div>
    </div>
  );

  return (
    <div className="pt-20 min-h-screen bg-[#F7F9FC]" data-testid="order-confirmation-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-sm shadow-sm border border-slate-100 p-8 text-center mb-8">
          <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-[#0E518D] mb-2">{s.confirm_title || ''}</h1>
          <p className="text-[#475569] mb-2">{s.confirm_text || ''}</p>
          <p className="text-sm text-slate-400">Vorgangsnummer: <span className="font-semibold text-[#0E518D]">{order.order_number}</span></p>
        </div>

        <div className="bg-white rounded-sm shadow-sm border border-slate-100 p-6" data-testid="order-details">
          <h2 className="text-xl font-semibold text-[#1E293B] mb-4 flex items-center gap-2">
            <Package className="h-5 w-5 text-[#0E518D]" />Bestelldetails
          </h2>
          <div className="grid grid-cols-2 gap-4 mb-6 text-sm">
            <div><span className="text-slate-400">Name:</span><p className="font-medium text-[#1E293B]">{order.first_name} {order.last_name}</p></div>
            <div><span className="text-slate-400">E-Mail:</span><p className="font-medium text-[#1E293B]">{order.email}</p></div>
            <div><span className="text-slate-400">Buchungsnr.:</span><p className="font-medium text-[#1E293B]">{order.booking_number}</p></div>
            <div><span className="text-slate-400">Anreise:</span><p className="font-medium text-[#1E293B]">{formatDate(order.arrival_date)}</p></div>
            <div><span className="text-slate-400">Lieferart:</span><p className="font-medium text-[#1E293B]">{order.delivery_type === 'pickup' ? (s.delivery_pickup || '') : (s.delivery_apartment || '')}</p></div>
          </div>
          <Separator className="my-4" />
          <h3 className="font-semibold text-[#1E293B] mb-3">Produkte</h3>
          <div className="space-y-2">
            {order.items?.map((item, i) => (
              <div key={i} className="flex justify-between items-center bg-slate-50 rounded-sm p-3 text-sm">
                <div>
                  <span className="font-medium text-[#1E293B]">{item.name}</span>
                  <span className="text-slate-400 ml-2">Art. {item.article_number}</span>
                  <span className="text-slate-400 ml-2">x {item.quantity}</span>
                </div>
                <span className="font-bold text-[#0E518D]">{item.total?.toFixed(2)} EUR</span>
              </div>
            ))}
          </div>
          <Separator className="my-4" />
          <div className="flex justify-between items-center">
            <span className="font-semibold text-[#1E293B]">Gesamtsumme</span>
            <span className="text-2xl font-bold text-[#0E518D]">{order.total?.toFixed(2)} EUR</span>
          </div>
          <p className="text-xs text-slate-400 mt-3 italic">{s.confirm_payment_hint || ''}</p>
        </div>

        <div className="mt-8 text-center">
          <Link to="/">
            <Button className="bg-[#0E518D] hover:bg-[#0B4171] text-white rounded-sm uppercase tracking-wide" data-testid="back-to-home">
              <ArrowLeft className="w-4 h-4 mr-2" /> {s.checkout_back || ''}
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
